import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js'
import options from './js/options.js'
import light from './js/light.js'
import loadingManager from './js/loader.js'
import { pdfCreate } from './js/pdf.js'
import {
    EffectComposer,
    RenderPass,
    SSAOPass,
    SMAAPass,
    OutputPass,
    UnrealBloomPass,
    RGBELoader
} from 'three/addons'
import theme from './js/theme.js'
import {initAIImageGenerator} from "./js/ai.js";
// import { GUI } from 'three/examples/jsm/libs/lil-gui.module.min.js'

// const gui = new GUI()

// ─── ОПРЕДЕЛЕНИЕ ПЛАТФОРМЫ ────────────────────

const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent)
const isIOS    = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream
const isMobile = isIOS || /android/i.test(navigator.userAgent)
const isLowEnd = isIOS || isSafari  // Safari desktop тоже имеет ограничения

// ─── СЦЕНА ───────────────────────────────────

const canvasContainer = document.querySelector('#canvas-container')
const canvas = document.querySelector('canvas.webgl')
const scene = new THREE.Scene()
const isLarge = canvasContainer.getBoundingClientRect().width > 600
const sizes = {
    width:  canvasContainer.getBoundingClientRect().width,
    height: canvasContainer.getBoundingClientRect().height
}

// ─── ЗАГРУЗЧИКИ ───────────────────────────────

const dracoLoader = new DRACOLoader(loadingManager)
dracoLoader.setDecoderPath('./draco/')
const gltfLoader = new GLTFLoader(loadingManager)
gltfLoader.setDRACOLoader(dracoLoader)

// ─── КАМЕРА И КОНТРОЛЫ ────────────────────────

const camera = new THREE.PerspectiveCamera(38, sizes.width / sizes.height, 0.3, 100)
camera.position.set(...(isLarge ? [5, 5, 16] : [5, 5, 22]))
scene.add(camera)

const controls = new OrbitControls(camera, canvas)
controls.target.set(0, 0.75, 0)
controls.enableDamping = true
controls.dampingFactor = 0.04
controls.enablePan = false
controls.minDistance = 1
controls.maxDistance = isLarge ? 30 : 30
controls.maxPolarAngle = Math.PI / 2

// ─── НАЧАЛЬНАЯ ПОЗИЦИЯ КАМЕРЫ ─────────────────

const defaultCameraPos    = new THREE.Vector3(...(isLarge ? [-16.05, 7.04, 1.34] : [5, 5, 22]))
const defaultCameraTarget = new THREE.Vector3(0, 0.75, 0)

// ─── АВТО-ВРАЩЕНИЕ ────────────────────────────

let autoRotate = true
let autoRotateTimer = null
const ROTATE_SPEED = 0.0012

canvas.addEventListener('pointerdown', () => {
    autoRotate = false
    clearTimeout(autoRotateTimer)
})

// ─── РЕНДЕРЕР ─────────────────────────────────

const renderer = new THREE.WebGLRenderer({
    canvas,
    antialias: true,
    preserveDrawingBuffer: true,
    powerPreference: 'high-performance'
})
renderer.setSize(sizes.width, sizes.height)

// ФИКС iOS: убран SS_RATIO — он умножал все composer-буферы в 4 раза по площади.
// На iOS достаточно нативного pixelRatio, зажатого до 1. SMAA покрывает алиасинг.
const getPixelRatio = () => Math.min(window.devicePixelRatio, isLowEnd ? 1 : 2)
renderer.setPixelRatio(getPixelRatio())

renderer.shadowMap.enabled = true
renderer.shadowMap.type = THREE.PCFSoftShadowMap
renderer.setClearColor('#878c92')
renderer.toneMapping = THREE.ACESFilmicToneMapping
renderer.toneMappingExposure = 0.95
renderer.outputColorSpace = THREE.SRGBColorSpace

// ─── HDR ENVIRONMENT ──────────────────────────

const pmremGenerator = new THREE.PMREMGenerator(renderer)
pmremGenerator.compileEquirectangularShader()
let globalEnvMap = null

new RGBELoader().load('./models/citrus_orchard_road_puresky_2k.hdr', (texture) => {
    texture.mapping = THREE.EquirectangularReflectionMapping
    globalEnvMap = pmremGenerator.fromEquirectangular(texture).texture
    scene.environment = globalEnvMap
    scene.environmentIntensity = 1.0
    texture.dispose()
    pmremGenerator.dispose()

    scene.traverse((child) => {
        if (child.isMesh && child.userData.isGlass) {
            const mats = Array.isArray(child.material) ? child.material : [child.material]
            mats.forEach(m => { m.envMap = globalEnvMap; m.needsUpdate = true })
        }
    })
})

// ─── ОСВЕЩЕНИЕ ────────────────────────────────

light(scene)

const sunLight = new THREE.DirectionalLight(0xffeedd, 1.9)
sunLight.position.set(8, 12, 8)
sunLight.castShadow = true
sunLight.shadow.mapSize.set(isLowEnd ? 1024 : 4096, isLowEnd ? 1024 : 4096)
sunLight.shadow.camera.near   = 1
sunLight.shadow.camera.far    = 50
sunLight.shadow.camera.left   = -12
sunLight.shadow.camera.right  =  12
sunLight.shadow.camera.top    =  12
sunLight.shadow.camera.bottom = -12
sunLight.shadow.bias       = -0.0005
sunLight.shadow.normalBias = 0.04
sunLight.shadow.radius     = 2.0
scene.add(sunLight)

const hemiLight = new THREE.HemisphereLight(0xbdd4f0, 0xb89870, 0.8)
scene.add(hemiLight)

const fillRight = new THREE.DirectionalLight(0xd8e8f8, 3)
fillRight.position.set(-6, 5, -3)
scene.add(fillRight)

const bounceLight = new THREE.DirectionalLight(0xffe4c0, 0.4)
bounceLight.position.set(1, -2, 5)
scene.add(bounceLight)

const shadowPlane = new THREE.Mesh(
    new THREE.PlaneGeometry(40, 40),
    new THREE.ShadowMaterial({ opacity: 0.18 })
)
shadowPlane.rotation.x = -Math.PI / 2
shadowPlane.position.y = -0.001
shadowPlane.receiveShadow = true
scene.add(shadowPlane)

// ─── POST-PROCESSING ──────────────────────────

// ФИКС iOS: Safari/iOS не поддерживает MSAA RenderTarget и HalfFloatType —
// используем UnsignedByteType и samples:0 на isLowEnd (уже было правильно).
const renderTarget = new THREE.WebGLRenderTarget(sizes.width, sizes.height, {
    samples: isLowEnd ? 8 : 8,
    type:    isLowEnd ? THREE.UnsignedByteType : THREE.HalfFloatType,
})
const composer = new EffectComposer(renderer, renderTarget)
composer.addPass(new RenderPass(scene, camera))

// ФИКС iOS: SSAOPass создаётся ТОЛЬКО на десктопе. Даже с enabled=false
// конструктор аллоцирует несколько внутренних render target-ов (~40-80 МБ) —
// на iOS это само по себе могло вызывать краш.
let ssaoPass = null
if (!isLowEnd) {
    ssaoPass = new SSAOPass(scene, camera, sizes.width, sizes.height)
    ssaoPass.kernelRadius = 0.3
    ssaoPass.minDistance  = 0.005
    ssaoPass.maxDistance  = 0.1
    ssaoPass.output = SSAOPass.OUTPUT.Default
    composer.addPass(ssaoPass)
}

// SMAA — на iOS pixelRatio уже зажат до 1, умножать не нужно
composer.addPass(new SMAAPass(sizes.width * getPixelRatio(), sizes.height * getPixelRatio()))

// ФИКС iOS: Bloom на iOS отключён (strength=0) — он создаёт доп. буферы
// половинного разрешения, которые в сумме с остальным давят на лимит 256 МБ.
const bloomPass = new UnrealBloomPass(
    new THREE.Vector2(sizes.width, sizes.height),
    isLowEnd ? 0.0 : 0.03,
    0.2,
    1.0
)
composer.addPass(bloomPass)
composer.addPass(new OutputPass())

// ─── ФОН ─────────────────────────────────────

const backgroundMaterial = new THREE.ShaderMaterial({
    depthTest: true,
    depthWrite: false,
    uniforms: {
        centerColor: { value: new THREE.Color(0xeaeaea) },
        edgeColor:   { value: new THREE.Color(0xcfcfcf) },
        uAspect:     { value: sizes.width / sizes.height }
    },
    vertexShader: `
        varying vec2 vUv;
        void main() {
            vUv = uv;
            gl_Position = vec4(position.xy, 0.9999, 1.0);
        }`,
    fragmentShader: `
        varying vec2 vUv;
        uniform vec3 centerColor;
        uniform vec3 edgeColor;
        uniform float uAspect;
        float random(vec2 c) {
            return fract(sin(dot(c, vec2(12.9898, 78.233))) * 43758.5453);
        }
        void main() {
            vec2 uv = vUv;
            uv.x = (vUv.x - 0.5) * uAspect + 0.5;
            float factor = smoothstep(0.25, 1.1, distance(uv, vec2(0.5, 0.46)));
            vec3 color = mix(centerColor, edgeColor, factor * 0.55);
            gl_FragColor = vec4(color + (random(vUv) - 0.5) * (2.0 / 255.0), 1.0);
        }`
})

const backgroundMesh = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), backgroundMaterial)
backgroundMesh.frustumCulled = false
backgroundMesh.position.z = -50
scene.add(backgroundMesh)

theme(scene, backgroundMaterial)

// ─── УТИЛИТА: правильная фильтрация текстуры ──

function fixTexture(texture, maxAniso) {
    if (!texture) return
    // На мобильном высокая анизотропия сама по себе даёт шиммер
    texture.anisotropy       = isMobile ? Math.min(4, maxAniso) : maxAniso
    texture.minFilter        = THREE.LinearMipmapLinearFilter
    texture.magFilter        = THREE.LinearFilter
    texture.generateMipmaps  = true
    texture.needsUpdate      = true
}

// ─── МАТЕРИАЛЫ ────────────────────────────────

const MATERIAL_MAP = {
    wood_inside:   { roughness: 0.85, metalness: 0.0,  envMapIntensity: 0.22 },
    wood_dark:     { roughness: 0.82, metalness: 0.0,  envMapIntensity: 0.30 },
    wood_black:    { roughness: 0.75, metalness: 0.0,  envMapIntensity: 0.38 },
    metal_black:   { roughness: 0.42, metalness: 0.82, envMapIntensity: 0.90 },
    dark:          { roughness: 0.92, metalness: 0.0,  envMapIntensity: 0.05 },
    floor:         { roughness: 0.80, metalness: 0.0,  envMapIntensity: 0.18 },
    rubber:        { roughness: 0.96, metalness: 0.0,  envMapIntensity: 0.02 },
    plastic_black: { roughness: 0.70, metalness: 0.0,  envMapIntensity: 0.12 },
}

const glassCache = new Map()
const glassSettings = {
    color:           '#888888',
    opacity:         0.1,
    roughness:       0.05,
    metalness:       0.0,
    reflectivity:    1.0,
    ior:             1.52,
    envMapIntensity: 3.0,
}

function getGlassMaterial(uuid) {
    if (!glassCache.has(uuid)) {
        glassCache.set(uuid, new THREE.MeshPhysicalMaterial({
            color:           new THREE.Color(glassSettings.color),
            transparent:     true,
            opacity:         glassSettings.opacity,
            roughness:       glassSettings.roughness,
            metalness:       glassSettings.metalness,
            reflectivity:    glassSettings.reflectivity,
            ior:             glassSettings.ior,
            envMapIntensity: glassSettings.envMapIntensity,
            depthWrite:      false,
            side:            THREE.DoubleSide,
            envMap:          globalEnvMap,
        }))
    }
    return glassCache.get(uuid)
}

function getAllGlassMaterials() {
    return [...glassCache.values()]
}

// const glassFolder = gui.addFolder('Стекло')
// glassFolder.addColor(glassSettings, 'color').name('Цвет')
//     .onChange(v => getAllGlassMaterials().forEach(m => { m.color.set(v); m.needsUpdate = true }))
// glassFolder.add(glassSettings, 'opacity',         0,   1,    0.01).name('Прозрачность')
//     .onChange(v => getAllGlassMaterials().forEach(m => { m.opacity = v; m.needsUpdate = true }))
// glassFolder.add(glassSettings, 'roughness',        0,   1,    0.01).name('Шероховатость')
//     .onChange(v => getAllGlassMaterials().forEach(m => { m.roughness = v; m.needsUpdate = true }))
// glassFolder.add(glassSettings, 'metalness',        0,   1,    0.01).name('Металличность')
//     .onChange(v => getAllGlassMaterials().forEach(m => { m.metalness = v; m.needsUpdate = true }))
// glassFolder.add(glassSettings, 'reflectivity',     0,   1,    0.01).name('Отражаемость')
//     .onChange(v => getAllGlassMaterials().forEach(m => { m.reflectivity = v; m.needsUpdate = true }))
// glassFolder.add(glassSettings, 'ior',              1,   2.33, 0.01).name('IOR')
//     .onChange(v => getAllGlassMaterials().forEach(m => { m.ior = v; m.needsUpdate = true }))
// glassFolder.add(glassSettings, 'envMapIntensity',  0,  10,    0.1 ).name('EnvMap')
//     .onChange(v => getAllGlassMaterials().forEach(m => { m.envMapIntensity = v; m.needsUpdate = true }))

// ─── ТОЧКИ КАМЕРЫ ────────────────────────────

const cameraPoints = [
    {
        label: '1',
        position: new THREE.Vector3(0, 1, 0),
        cameraPos: new THREE.Vector3(2, 2, 1.4),
    },
    {
        label: '2',
        position: new THREE.Vector3(-2.80, 1.06, 3.93),
        cameraPos: new THREE.Vector3(-9.86, 3.13, 10.47),
    },
    {
        label: '3',
        position: new THREE.Vector3(1.97, 2.19, -1.97),
        cameraPos: new THREE.Vector3(13.03, 6.67, -9.71),
    },
]

const pointStyle = `
    position: absolute;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: rgba(255,255,255,0.92);
    border: 2px solid rgba(0,0,0,0.15);
    box-shadow: 0 2px 8px rgba(0,0,0,0.25);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    font-weight: 600;
    color: #222;
    cursor: pointer;
    transform: translate(-50%, -50%);
    transition: background 0.2s, transform 0.15s;
    user-select: none;
    z-index: 10;
`

canvasContainer.style.position = 'relative'

const pointEls = cameraPoints.map((pt) => {
    const el = document.createElement('div')
    el.style.cssText = pointStyle
    el.textContent = pt.label
    el.title = `Point ${pt.label}`
    el.addEventListener('mouseenter', () => { el.style.background = 'rgba(255,255,255,1)'; el.style.transform = 'translate(-50%, -50%) scale(1.15)' })
    el.addEventListener('mouseleave', () => { el.style.background = 'rgba(255,255,255,0.92)'; el.style.transform = 'translate(-50%, -50%) scale(1)' })
    el.addEventListener('click', () => animateCamera(pt.cameraPos, pt.position))
    canvasContainer.appendChild(el)
    return { el, worldPos: pt.position }
})

// ─── КОНТЕЙНЕР КНОПОК ────────────────────────

const btnContainer = document.createElement('div')
btnContainer.style.cssText = `
    position: absolute;
    bottom: 20px;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    gap: 10px;
    z-index: 10;
`
canvasContainer.appendChild(btnContainer)

const btnStyle = `
    padding: 8px 20px;
    background: rgba(255,255,255,0.92);
    border: 2px solid rgba(0,0,0,0.12);
    border-radius: 20px;
    font-size: 14px;
    font-weight: 600;
    color: #222;
    cursor: pointer;
    box-shadow: 0 2px 8px rgba(0,0,0,0.2);
    transition: background 0.2s;
    white-space: nowrap;
`

const resetBtn = document.createElement('button')
resetBtn.textContent = 'Reset view'
resetBtn.style.cssText = btnStyle
resetBtn.addEventListener('mouseenter', () => { resetBtn.style.background = 'rgba(255,255,255,1)' })
resetBtn.addEventListener('mouseleave', () => { resetBtn.style.background = 'rgba(255,255,255,0.92)' })
resetBtn.addEventListener('click', () => animateCamera(defaultCameraPos, defaultCameraTarget))
btnContainer.appendChild(resetBtn)

let pointsVisible = true
const toggleBtn = document.createElement('button')
toggleBtn.textContent = 'Hide points'
toggleBtn.style.cssText = btnStyle
toggleBtn.addEventListener('mouseenter', () => { toggleBtn.style.background = 'rgba(255,255,255,1)' })
toggleBtn.addEventListener('mouseleave', () => { toggleBtn.style.background = pointsVisible ? 'rgba(255,255,255,0.92)' : 'rgba(0,0,0,0.15)' })
toggleBtn.addEventListener('click', () => {
    pointsVisible = !pointsVisible
    pointEls.forEach(({ el }) => { el.style.display = pointsVisible ? 'flex' : 'none' })
    toggleBtn.style.background = pointsVisible ? 'rgba(255,255,255,0.92)' : 'rgba(0,0,0,0.15)'
    toggleBtn.style.color = pointsVisible ? '#222' : '#fff'
    toggleBtn.textContent = pointsVisible ? 'Hide points' : 'Show points'
})
btnContainer.appendChild(toggleBtn)

// ─── АНИМАЦИЯ КАМЕРЫ ─────────────────────────

function animateCamera(targetPos, targetLookAt) {
    autoRotate = false
    const startPos    = camera.position.clone()
    const startTarget = controls.target.clone()
    const duration    = 900
    const startTime   = performance.now()

    function animStep() {
        const t     = Math.min((performance.now() - startTime) / duration, 1)
        const eased = t < 0.5 ? 4*t*t*t : 1 - Math.pow(-2*t + 2, 3) / 2
        camera.position.lerpVectors(startPos, targetPos, eased)
        controls.target.lerpVectors(startTarget, targetLookAt, eased)
        controls.update()
        if (t < 1) requestAnimationFrame(animStep)
    }
    animStep()
}

// ─── ОБНОВЛЕНИЕ ПОЗИЦИЙ ТОЧЕК НА ЭКРАНЕ ──────

const _vec = new THREE.Vector3()

function updatePointPositions() {
    pointEls.forEach(({ el, worldPos }) => {
        if (!pointsVisible) return
        _vec.copy(worldPos).project(camera)
        if (_vec.z > 1) {
            el.style.display = 'none'
            return
        }
        el.style.display = 'flex'
        el.style.left = `${( _vec.x * 0.5 + 0.5) * sizes.width}px`
        el.style.top  = `${(-_vec.y * 0.5 + 0.5) * sizes.height}px`
    })
}

// ─── МОДЕЛЬ ───────────────────────────────────

const sceneSettings = { panelColor: '#1A1A1A', wallColor: '#202020' }
let panelMaterials   = []
let wallMaterial     = null
let model            = null
let originalMaterial = null

gltfLoader.load('./models/house.glb', async (gltf) => {
    model = gltf.scene

    model.traverse((child) => {
        const name = child.name.toLowerCase()
        if (name.includes('shade_deck') || name.includes('split_cover')) {
            child.visible = false
            child.castShadow = false
            return
        }
        if (!child.isMesh) return

        child.castShadow    = child.visible
        child.receiveShadow = true

        const maxAniso = renderer.capabilities.getMaxAnisotropy()
        const isArray  = Array.isArray(child.material)
        const mats     = isArray ? child.material : [child.material]

        const newMats = mats.map(mat => {
            const matName = mat.name.toLowerCase()

            // ─── ИСПРАВЛЕНИЕ РЯБИ: правильная фильтрация всех текстур ───
            fixTexture(mat.map,             maxAniso)
            fixTexture(mat.roughnessMap,    maxAniso)
            fixTexture(mat.metalnessMap,    maxAniso)
            fixTexture(mat.aoMap,           maxAniso)
            fixTexture(mat.emissiveMap,     maxAniso)
            fixTexture(mat.bumpMap,         maxAniso)
            fixTexture(mat.displacementMap, maxAniso)

            // ─── ДЕРЕВО: отключаем карты которые дают рябь ───────────────
            const isWood = matName.includes('wood')
            if (isWood) {
                mat.normalMap   = null
                mat.needsUpdate = true
            } else {
                fixTexture(mat.normalMap, maxAniso)
            }
            // ─────────────────────────────────────────────────────────────

            if (matName.includes('panelblack')) {
                mat.color.set(sceneSettings.panelColor)
                if (!panelMaterials.includes(mat)) panelMaterials.push(mat)
            }

            if (name === 'wall' || matName === 'wall') wallMaterial = mat

            if (matName === 'glass') {
                child.userData.isGlass = true
                return getGlassMaterial(mat.uuid)
            }

            return mat
        })

        child.material = isArray ? newMats : newMats[0]
        if (child.material?.name === 'Wood_outside') originalMaterial = child.material
    })

    model.position.y = 0
    scene.add(model)

    // const colorFolder = gui.addFolder('Цвета')
    // if (panelMaterials.length > 0) {
    //     colorFolder.addColor(sceneSettings, 'panelColor').name('Фасады')
    //         .onChange(v => panelMaterials.forEach(m => m.color.set(v)))
    // }
    // if (wallMaterial) {
    //     colorFolder.addColor(sceneSettings, 'wallColor').name('Стена')
    //         .onChange(v => wallMaterial.color.set(v))
    // }

    options(model, originalMaterial, scene, camera)
    await pdfCreate(renderer, camera, scene, model, backgroundMaterial)

    initAIImageGenerator({
        renderer,
        camera,
        scene,
    });
})

// ─── RESIZE ───────────────────────────────────

window.addEventListener('resize', () => {
    sizes.width  = canvasContainer.clientWidth
    sizes.height = canvasContainer.clientHeight
    camera.aspect = sizes.width / sizes.height
    camera.updateProjectionMatrix()

    // ФИКС iOS: убран SS_RATIO и composer.setPixelRatio —
    // setPixelRatio на composer умножает размеры всех внутренних буферов,
    // что в связке с SS_RATIO давало ×4 по площади и убивало память на iOS.
    const pr = getPixelRatio()
    renderer.setSize(sizes.width, sizes.height)
    renderer.setPixelRatio(pr)
    composer.setSize(sizes.width, sizes.height)
    backgroundMaterial.uniforms.uAspect.value = sizes.width / sizes.height
})

// ─── GUI ──────────────────────────────────────

// gui.addFolder('Рендерер')
//     .add(renderer, 'toneMappingExposure', 0.3, 2.0, 0.01).name('Экспозиция')
//
// // ФИКС iOS: SSAO GUI только если pass был создан (только на десктопе)
// if (ssaoPass) {
//     const ssaoFolder = gui.addFolder('SSAO')
//     ssaoFolder.add(ssaoPass, 'enabled').name('Вкл/Выкл')
//     ssaoFolder.add(ssaoPass, 'kernelRadius', 0, 1,    0.01  ).name('Радиус')
//     ssaoFolder.add(ssaoPass, 'minDistance',  0, 0.05, 0.0001).name('Мин. дист')
//     ssaoFolder.add(ssaoPass, 'maxDistance',  0, 0.1,  0.001 ).name('Макс. дист')
// }
//
// const bloomFolder = gui.addFolder('Bloom')
// bloomFolder.add(bloomPass, 'strength',  0, 1.0, 0.01).name('Сила')
// bloomFolder.add(bloomPass, 'radius',    0, 1.5, 0.01).name('Радиус')
// bloomFolder.add(bloomPass, 'threshold', 0, 1.0, 0.01).name('Порог')
//
// const lightFolder = gui.addFolder('Освещение')
// lightFolder.add(sunLight,    'intensity', 0, 6,   0.05).name('Солнце')
// lightFolder.add(hemiLight,   'intensity', 0, 3,   0.05).name('Небо')
// lightFolder.add(fillRight,   'intensity', 0, 8,   0.05).name('Fill правый')
// lightFolder.add(bounceLight, 'intensity', 0, 1.5, 0.05).name('Отражение')
// lightFolder.add({ env: 1.0 }, 'env', 0, 3, 0.05).name('HDR')
//     .onChange(v => scene.environmentIntensity = v)
//
// const bgFolder = gui.addFolder('Фон')
// bgFolder.addColor({ c: '#eaeaea' }, 'c').name('Центр')
//     .onChange(v => backgroundMaterial.uniforms.centerColor.value.set(v))
// bgFolder.addColor({ e: '#cfcfcf' }, 'e').name('Края')
//     .onChange(v => backgroundMaterial.uniforms.edgeColor.value.set(v))

// ─── АНИМАЦИЯ ─────────────────────────────────

const tick = () => {
    controls.update()
    if (autoRotate && model) model.rotation.y += ROTATE_SPEED
    updatePointPositions()
    composer.render()
    window.requestAnimationFrame(tick)
}

tick()